/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The owner-figure vocabulary shared by the rail card (`OwnerRailAsk`), the reader under the
 * gallery (`OwnerNotesReader`) and the band's example (`Aggregate` in `OwnerFigures.tsx`):
 * every figure a count from the FIRST note, percentages from OWNER_PERCENT_MIN_NOTES, callouts
 * from structured answers only — the hours median against the spec's claim, the wear-out picks —
 * never a sentence synthesised from note text, and never a comparison with the review score.
 * Owners answer against their OWN expectations. Pure; the components are in `OwnerFigures.tsx`.
 */
import {
  OWNER_DEFAULT_QUESTIONS,
  type OwnerAggregate,
  type OwnerQuestionSetDef,
  type OwnerReadAggregate,
} from 'lib/dealhunt/types'
import { lowerFirst } from './band-copy'
import { READ_WORD } from './card-owners'

function pct(n: number, total: number): string {
  return `${total > 0 ? Math.round((n / total) * 100) : 0}%`
}

/** "5 as expected · 2 worse" — zero segments are left out; percentages past the gate. */
export function readSplit(read: OwnerReadAggregate, gateMet: boolean): string {
  const part = (n: number, word: string): string | null => {
    if (n <= 0) {
      return null
    }
    return `${gateMet ? pct(n, read.answers) : n} ${word}`
  }
  return [
    part(read.better, READ_WORD.better),
    part(read.expected, READ_WORD.expected),
    part(read.worse, READ_WORD.worse),
  ]
    .filter(Boolean)
    .join(' · ')
}

/** "owned 1–6 months to over 2 years" from the range; the median bucket when an older DealHunt sent none. */
export function ownedLine(aggregate: OwnerAggregate): string | null {
  const range = aggregate.ownership_range
  if (range) {
    return range.min.id === range.max.id
      ? `owned ${lowerFirst(range.min.label)}`
      : `owned ${lowerFirst(range.min.label)} to ${lowerFirst(range.max.label)}`
  }
  const median = aggregate.median_ownership_bucket
  const label = median
    ? OWNER_DEFAULT_QUESTIONS.ownership_length.find(o => o.id === median)?.label
    : undefined
  return label ? `typically owned ${lowerFirst(label)}` : null
}

/** "n owner reviews · k photo-verified · owned A to B" */
export function metaLine(aggregate: OwnerAggregate): string {
  const total = aggregate.published_count
  return [
    `${total} owner review${total === 1 ? '' : 's'}`,
    aggregate.photo_verified > 0 ? `${aggregate.photo_verified} photo-verified` : null,
    ownedLine(aggregate),
  ]
    .filter(Boolean)
    .join(' · ')
}

/** The buy-again figure the dial shows, then the rest of the sentence. */
export interface OwnerSentenceParts {
  lead: string
  rest: string
}

/**
 * The owner sentence beside the dial: "6 of 7 would buy again. 5 of 7 still use it daily,
 * 1 sometimes, 1 stopped." — percentages past the gate; the still-using half from the set's own
 * options in the set's order, zero options left out, absent when DealHunt sent no counts. One
 * owner reads as one owner: "Would buy it again and still uses it daily." The lead is the
 * buy-again figure (the dial's own number), highlighted the way the hero's percentile is.
 */
export function ownerSentence(aggregate: OwnerAggregate, gateMet: boolean): OwnerSentenceParts {
  const total = aggregate.published_count
  const opts = aggregate.still_using ?? []
  if (total === 1) {
    const lead = aggregate.buy_again_yes === 1 ? 'Would buy it again' : 'Would not buy it again'
    const using = opts.find(o => o.count === 1)
    if (!using) {
      return { lead, rest: '.' }
    }
    if (using.id === 'yes') {
      return { lead, rest: ' and still uses it daily.' }
    }
    if (using.id === 'no') {
      return { lead, rest: '; has stopped using it.' }
    }
    return { lead, rest: `; still uses it ${lowerFirst(using.label)}.` }
  }
  const figure = (n: number): string => (gateMet ? pct(n, total) : `${n} of ${total}`)
  const lead = figure(aggregate.buy_again_yes)
  const daily = opts.find(o => o.id === 'yes')
  if (!daily) {
    return { lead, rest: ' would buy again.' }
  }
  const rest = opts
    .filter(o => o.id !== 'yes' && o.count > 0)
    .map(o => `${gateMet ? pct(o.count, total) : o.count} ${lowerFirst(restWord(o.id, o.label))}`)
  return {
    lead,
    rest: ` would buy again. ${figure(daily.count)} still use it daily${
      rest.length ? `, ${rest.join(', ')}` : ''
    }.`,
  }
}

/** "Sometimes" stays; "No, stopped" → "stopped" — the option label minus its yes/no prefix. */
function restWord(id: string, label: string): string {
  if (id === 'no') {
    return label.replace(/^no,\s*/i, '')
  }
  return label
}

/** The hours callout, the two lines the design splits it into. */
export function hoursCallout(
  aggregate: OwnerAggregate
): { lead: string; rest: string; fine: string } | null {
  if (aggregate.hours_median === null) {
    return null
  }
  const n = aggregate.hours_count
  const sample =
    typeof n === 'number' && n > 0
      ? `Median of ${n} owner${n === 1 ? '' : 's'} who reported hours`
      : 'Median of the owners who reported hours'
  return {
    lead: `~${aggregate.hours_median} h per charge`,
    rest:
      aggregate.claimed_hours !== null
        ? ` reported vs ${aggregate.claimed_hours} h claimed`
        : ' reported',
    fine: aggregate.claimed_hours !== null ? `${sample} · claim from the spec sheet` : sample,
  }
}

/** The wear-out callout: the picks, and how many of those asked picked "Nothing yet". */
export function gaveOutCallout(aggregate: OwnerAggregate): { text: string; fine: string } | null {
  const picks = aggregate.gave_out.filter(g => g.count > 0)
  if (picks.length === 0) {
    return null
  }
  const nothing = aggregate.gave_out_nothing
  const asked = picks.reduce((sum, g) => sum + g.count, 0) + (nothing ?? 0)
  return {
    text: picks.map(g => `${g.label} (${g.count})`).join(' · '),
    fine:
      typeof nothing === 'number'
        ? `Structured answers · ${nothing} of ${asked} asked picked “Nothing yet”`
        : 'Structured answers from owners who found something worse than expected',
  }
}

export const READS_FOOTNOTE =
  'Reads are set per category and appear only once answered. Owners compare against what they expected, not against the review score.'

/**
 * Labels for the slides' chips and ownership line without a question-set fetch: the default set
 * with the aggregate's live read labels over it. Ids are stable across set versions, so a
 * category set's other options still label from the default when they share ids.
 */
export function questionsFor(aggregate: OwnerAggregate | null): OwnerQuestionSetDef {
  if (!aggregate?.reads.length) {
    return OWNER_DEFAULT_QUESTIONS
  }
  return {
    ...OWNER_DEFAULT_QUESTIONS,
    reads: aggregate.reads.map(r => ({ id: r.id, label: r.label })),
  }
}
